import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import LandingUnik from "./pages/LandingUnik";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ClientDashboard from "./pages/client/Dashboard";
import Movements from "./pages/client/Movements";
import Transfers from "./pages/client/Transfers";
import Loans from "./pages/client/Loans";
import Profile from "./pages/client/Profile";
import ChatPage from "./pages/ChatPage";
import AdvisorOverview from "./pages/advisor/Overview";
import AdvisorClients from "./pages/advisor/Clients";
import ClientDetail from "./pages/advisor/ClientDetail";
import LoanRequests from "./pages/advisor/LoanRequests";
import PendingTransfers from "./pages/advisor/PendingTransfers";
import LoanSimulator from "./pages/advisor/Simulator";
import AdminOverview from "./pages/admin/Overview";
import AdminUsers from "./pages/admin/Users";
import AdminLoans from "./pages/admin/Loans";
import AdminAudit from "./pages/admin/Audit";
import AdminBackup from "./pages/admin/Backup";

function Home() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <AdminOverview />;
  if (user.role === "advisor") return <AdvisorOverview />;
  return <ClientDashboard />;
}

function RoleGate({ roles, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  return (
    <ErrorBoundary locationKey={location.pathname}>
      <Routes>
        <Route path="/" element={<LandingUnik />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        <Route path="/app" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="perfil" element={<Profile />} />

          {/* Client */}
          <Route path="movimientos" element={<RoleGate roles={["client"]}><Movements /></RoleGate>} />
          <Route path="transferencias" element={<RoleGate roles={["client"]}><Transfers /></RoleGate>} />
          <Route path="creditos" element={<RoleGate roles={["client"]}><Loans /></RoleGate>} />

          {/* Advisor */}
          <Route path="clientes" element={<RoleGate roles={["advisor", "admin"]}><AdvisorClients /></RoleGate>} />
          <Route path="clientes/:id" element={<RoleGate roles={["advisor", "admin"]}><ClientDetail /></RoleGate>} />
          <Route path="solicitudes" element={<RoleGate roles={["advisor", "admin"]}><LoanRequests /></RoleGate>} />
          <Route path="autorizaciones" element={<RoleGate roles={["advisor", "admin"]}><PendingTransfers /></RoleGate>} />
          <Route path="simulador" element={<RoleGate roles={["advisor", "admin"]}><LoanSimulator /></RoleGate>} />

          {/* Admin */}
          <Route path="usuarios" element={<RoleGate roles={["admin"]}><AdminUsers /></RoleGate>} />
          <Route path="creditos-admin" element={<RoleGate roles={["admin"]}><AdminLoans /></RoleGate>} />
          <Route path="auditoria" element={<RoleGate roles={["admin"]}><AdminAudit /></RoleGate>} />
          <Route path="respaldo" element={<RoleGate roles={["admin"]}><AdminBackup /></RoleGate>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
